import { Arg, COMPONENT_STATE_ARGS, Metadata, pick } from '../../../../utils/spds/metadata';
import { action } from '@ember/object';
import { fn } from '@ember/helper';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../../layout/flex/column.gjs';
import Message from './message.gjs';
import Super from '../../core/base.gjs';
import generateUUID from '../../../../utils/generate-uuid';

const METADATA = Metadata.define({
  description: `
Container that manages a collection of alert banner messages, dispatched via the alert manager service
  `,
  extends: [
    pick(COMPONENT_STATE_ARGS, [
      {
        name: 'id',
        description: 'Unique identifier for the alert banner container instance',
        required: true,
      },
    ]),
  ],
  meta: {
    gap: Arg.string({ default: '8px', description: 'Gap between stacked alert banners' }),
  },
  name: 'Alert Banner Container',
});

export default class SpdsFeedbackAlertBannerContainer extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-feedback-alert-banner-container';
  classesToMapToAttrs = ['hasContent'];

  @tracked alerts = [];

  @service alertManager;

  get gap() {
    return this.argOrDefault('gap');
  }

  get hasContent() {
    return this.alerts.length > 0;
  }

  constructor() {
    super(...arguments);

    this.alertManager.registerComponent(this);
    this.bindServiceEvents();
  }

  bindServiceEvents() {
    let instance = this.args.id;

    let events = [
      {
        context: this,
        event: 'clearBanner',
        handler: this.removeBanner,
        instance,
      },
      {
        context: this,
        event: 'displayBanner',
        handler: this.displayBanner,
        instance,
      },
    ];

    events.forEach(event => this.alertManager.bindEventHandler(event));
  }

  displayBanner(alert) {
    this.alerts = [...this.alerts, { id: generateUUID(), ...alert }];
  }

  removeBanner(alert) {
    let index = alert ? this.alerts.findIndex(({ id }) => id === alert.id) : 0;
    let removed = this.alerts.splice(index, 1)[0];

    this.alerts = [...this.alerts];

    removed.onClose?.();
  }

  @action
  dismissBanner(alert) {
    this.removeBanner(alert);
  }

  <template>
    <FlexColumn
      @gap={{this.gap}}
      class={{this.classes}}
      data-test-spds-feedback-alert-banner-container={{this.id}}
      id={{this.id}}
      ...attributes
    >
      {{#each this.alerts as |alert|}}
        <Message
          @body={{alert.body}}
          @ctaAlignment={{alert.ctaAlignment}}
          @header={{alert.header}}
          @includeIcon={{alert.includeIcon}}
          @onClose={{fn this.dismissBanner alert}}
          @onPrimaryBtnClick={{alert.onPrimaryBtnClick}}
          @onSecondaryBtnClick={{alert.onSecondaryBtnClick}}
          @primaryBtnText={{alert.primaryBtnText}}
          @secondaryBtnText={{alert.secondaryBtnText}}
          @type={{alert.type}}
        />
      {{/each}}
    </FlexColumn>
  </template>
}

export const ALERT_BANNER_CONTAINER_METADATA = SpdsFeedbackAlertBannerContainer.metadata;
